import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { sidebarConfig } from "../sidebarConfig";
import { DRAWER_WIDTH, MINI_DRAWER_WIDTH, HEADER_HEIGHT } from "./layoutConstants";

function useCurrentPageLabel() {
  const { pathname } = useLocation();
  const match = sidebarConfig.find((item) => pathname.startsWith(item.path));
  return match?.label || "Dashboard";
}

export default function AdminHeader({ collapsed, onMenuClick, user, onLogout }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const pageLabel = useCurrentPageLabel();
  const desktopWidth = collapsed ? MINI_DRAWER_WIDTH : DRAWER_WIDTH;

  const initials = (user?.fullName || "Admin")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  function confirmLogout() {
    setMenuAnchor(null);
    setLogoutOpen(true);
  }

  function handleLogout() {
    setLogoutOpen(false);
    onLogout();
  }

  return (
    <>
      <AppBar
        position="fixed"
        color="default"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${desktopWidth}px)` },
          ml: { md: `${desktopWidth}px` },
          bgcolor: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(8px)",
          borderBottom: "1px solid #e2e8f0",
          transition: (theme) =>
            theme.transitions.create(["width", "margin"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.standard,
            }),
        }}
      >
        <Toolbar sx={{ minHeight: `${HEADER_HEIGHT}px !important`, gap: 1.5 }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={onMenuClick}
            sx={{
              color: "text.secondary",
              "&:hover": { bgcolor: "rgba(15,23,42,0.06)" },
            }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: "text.disabled", fontWeight: 600, letterSpacing: "0.02em" }}>
              Admin Panel
            </Typography>
            <Typography variant="h6" noWrap fontWeight={700} sx={{ color: "text.primary", lineHeight: 1.2 }}>
              {pageLabel}
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Chip
            label="Live"
            size="small"
            sx={{
              display: { xs: "none", sm: "flex" },
              bgcolor: "rgba(22, 163, 74, 0.1)",
              color: "success.main",
              fontWeight: 600,
              fontSize: 12,
              "& .MuiChip-label": { px: 1.25 },
            }}
            icon={
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: "success.main",
                  ml: "8px !important",
                }}
              />
            }
          />

          <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" }, mx: 0.5, my: 1.5 }} />

          <Button
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{
              px: 1,
              py: 0.5,
              borderRadius: 2.5,
              textTransform: "none",
              color: "text.primary",
              "&:hover": { bgcolor: "rgba(15,23,42,0.06)" },
            }}
            endIcon={<KeyboardArrowDownIcon sx={{ color: "text.disabled" }} />}
          >
            <Avatar sx={{ width: 34, height: 34, bgcolor: "secondary.main", fontSize: 13, fontWeight: 700, mr: 1 }}>
              {initials}
            </Avatar>
            <Box sx={{ display: { xs: "none", sm: "block" }, textAlign: "left" }}>
              <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 140 }}>
                {user?.fullName || "Admin"}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }} noWrap>
                Administrator
              </Typography>
            </Box>
          </Button>

          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            slotProps={{ paper: { sx: { mt: 1, minWidth: 240 } } }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="body2" fontWeight={700}>
                {user?.fullName || "Admin"}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                {user?.email}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={confirmLogout} sx={{ color: "error.main", py: 1.25 }}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" sx={{ color: "error.main" }} />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Dialog
        open={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        slotProps={{ paper: { sx: { borderRadius: 3, minWidth: 340 } } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Log out of Admin Panel?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You'll need to sign in again to access the dashboard and manage the marketplace.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setLogoutOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleLogout} variant="contained" color="error" disableElevation>
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
